import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, TextInput, Animated, Easing, Alert } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import LoadingSpinner from '../components/LoadingSpinner';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import Svg, { Path, LinearGradient as SvgLinearGradient, Defs, Stop, G, ClipPath, Rect } from 'react-native-svg';
import { useForgotPassword } from '../hooks/usePasswordReset';
import GradientText from '../components/GradientText';
import { ScreenBackground } from '../components/ThemeGradients';
import { useUiStyle } from '../context/UiStyleContext';
import { useNavigation } from '../context/NavigationContext';
import { isMobile } from '../utils/responsive';
import { lookSheets, useLookStyles, useOriginalPolish } from './originalLook';


export const Input = ({ placeholder, value, onChangeText, keyboardType = 'default', secureTextEntry = false, disabled = false, placeholderTextColor = "rgba(255, 255, 255, 0.40)", label, autoComplete, onSubmitEditing }) => {
    const styles = useLookStyles(SHEETS);
    const { isFloodlight } = useUiStyle();
    const [isFocused, setIsFocused] = useState(false);
    const focusT = useRef(new Animated.Value(0)).current;
    const onFocus = () => {
        setIsFocused(true);
        Animated.timing(focusT, { toValue: 1, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };
    const onBlur = () => {
        setIsFocused(false);
        Animated.timing(focusT, { toValue: 0, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };
    return (
        <View style={[styles.inputContainer, isFocused && (isFloodlight ? styles.inputContainerFocused : { borderColor: 'rgba(255, 255, 255, 0.9)' })]}>
            <TextInput
                placeholder={placeholder}
                placeholderTextColor={placeholderTextColor}
                style={styles.input}
                value={value}
                onChangeText={onChangeText}
                keyboardType={keyboardType}
                secureTextEntry={secureTextEntry}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={disabled}
                {...(isFloodlight ? {
                    accessibilityLabel: label || placeholder,
                    autoComplete,
                    autoCapitalize: 'none',
                    onSubmitEditing,
                } : {})}
            />
        </View>
    )
};

const ForgotPasswordScreen = () => {
    const {
        email, setEmail, loading, agreedToTerms, setAgreedToTerms,
        error, success, handleResetPassword, goToLogin,
    } = useForgotPassword();
    const styles = useLookStyles(SHEETS);
    const { isFloodlight, still, root } = useOriginalPolish();
    const fl = (props) => (isFloodlight ? props : {});
    const { navigateTo } = useNavigation();

    // Animation values
    const pageOpacity = useRef(new Animated.Value(still ? 1 : 0)).current;
    const [isExiting, setIsExiting] = useState(false);

    // Page load animation (skipped under reduced motion in Floodlight)
    useEffect(() => {
        if (still) return;
        Animated.timing(pageOpacity, {
            toValue: 1,
            duration: 1000,
            easing: Easing.inOut(Easing.cubic),
            useNativeDriver: true,
        }).start();
    }, []);

    // Exit animation function
    const exitAnimation = (callback) => {
        setIsExiting(true);
        Animated.timing(pageOpacity, {
            toValue: 0,
            duration: 600,
            easing: Easing.in(Easing.cubic),
            useNativeDriver: true,
        }).start(() => {
            if (callback) callback();
        });
    };

    // Register exit animation with navigation context
    useEffect(() => {
        if (Platform.OS === 'web') {
            window.forgotPasswordExitAnimation = exitAnimation;
        }
        return () => {
            if (Platform.OS === 'web') {
                delete window.forgotPasswordExitAnimation;
            }
        };
    }, []);

    return (
        <View style={styles.container} {...root}>
            <ScreenBackground />
            <Animated.View style={[
                styles.mainContent,
                {
                    opacity: pageOpacity,
                }
            ]}>

                <View style={styles.formContainer}>
                    <BlurView intensity={20} tint="dark" style={styles.formCard}>
                        <View style={styles.formContent}>
                            <GradientText
                                style={styles.title}
                                fontSize={28}
                                fontWeight="500"
                                gradientColors={["#707070", "#DDDDDD"]}
                            >
                                Forgot Password?
                            </GradientText>
                            <View style={styles.formSection}>
                                <View style={styles.inputContainerWrapper}>

                                    <Input
                                        placeholder="Enter your email"
                                        placeholderTextColor="rgba(255, 255, 255, 0.20)"
                                        value={email}
                                        onChangeText={setEmail}
                                        keyboardType="email-address"
                                        disabled={loading || isExiting}
                                        label="Email"
                                        autoComplete="email"
                                        onSubmitEditing={() => { if (!(loading || isExiting || success)) handleResetPassword(); }}
                                    />

                                       <TouchableOpacity 
                                           style={[styles.primaryButton, (loading || isExiting) && styles.buttonDisabled]} 
                                           onPress={handleResetPassword}
                                           disabled={loading || isExiting || success}
                                           {...fl({ accessibilityRole: 'button', accessibilityLabel: success ? 'Email sent' : loading ? 'Sending' : 'Send reset email' })}
                                       >
                                           <LinearGradient colors={['rgba(0, 0, 0, 0.16)', 'rgba(116, 132, 154, 0.16)']} style={styles.buttonGradient}>
                                               {loading ? (
                                                   <View style={styles.loadingContainer}>
                                                       <LoadingSpinner size={16} color="rgba(255, 255, 255, 0.9)" />
                                                       <GradientText style={styles.primaryButtonText}>Sending...</GradientText>
                                                   </View>
                                               ) : success ? (
                                                   <GradientText style={styles.primaryButtonText}>Email Sent!</GradientText>
                                               ) : (
                                                   <GradientText style={styles.primaryButtonText}>Send Reset Email</GradientText>
                                               )}
                                           </LinearGradient>
                                       </TouchableOpacity>

                                       {error ? (
                                           <Text style={styles.errorText} {...fl({ accessibilityRole: 'alert' })}>{error}</Text>
                                       ) : success ? (
                                           <Text style={styles.successText} {...fl({ accessibilityRole: 'status' })}>
                                               Reset email sent! Check your inbox and return to sign in.
                                           </Text>
                                       ) : null}
                                </View>


                                <View style={styles.termsContainer}>
                                    <TouchableOpacity
                                        style={styles.checkboxContainer}
                                        onPress={() => setAgreedToTerms(!agreedToTerms)}
                                        disabled={loading || isExiting}
                                        {...fl({ accessibilityRole: 'checkbox', 'aria-checked': agreedToTerms, accessibilityLabel: 'I agree to the Terms and Conditions' })}
                                    >
                                        <View style={[styles.checkbox, agreedToTerms && styles.checkboxChecked]}>
                                            {agreedToTerms && (
                                                <Ionicons name="checkmark" size={12} color="rgba(255, 255, 255, 0.80)" />
                                            )}
                                        </View>
                                        <Text style={[styles.termsText, (loading || isExiting) && styles.textDisabled]}>
                                            I agree to the Terms and Conditions
                                        </Text>
                                    </TouchableOpacity>
                                    {isFloodlight ? (
                                        <TouchableOpacity onPress={() => navigateTo('Terms')} style={styles.termsLink} accessibilityRole="link" accessibilityLabel="Read the terms">
                                            <Text style={styles.termsLinkText}>Read the terms</Text>
                                        </TouchableOpacity>
                                    ) : null}
                                </View>

                            </View>
                            {isFloodlight ? (
                                <TouchableOpacity onPress={goToLogin} style={styles.backLink} accessibilityRole="link" accessibilityLabel="Back to sign in">
                                    <Text style={styles.backLinkText}>← Back to sign in</Text>
                                </TouchableOpacity>
                            ) : null}
                        </View>
                    </BlurView>
                </View>
            </Animated.View>
        </View>
    );
};

// Floodlight-only refinements on top of the Archivo retype (Classic never sees these).
const FL_OVERRIDES = {
    mainContent: isMobile ? { paddingHorizontal: 16, paddingTop: 24 } : {},
    formContainer: { minHeight: 0 },
    formCard: {
        height: 'auto',
        padding: isMobile ? 24 : 44,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.10)',
        overflow: 'hidden',
        ...(Platform.OS === 'web' ? { boxShadow: '0 24px 64px rgba(0,0,0,0.45)' } : {}),
    },
    title: { fontWeight: '700', letterSpacing: -0.6, lineHeight: 34 },
    inputContainer: { borderRadius: 12, minHeight: 46, justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.03)', ...(Platform.OS === 'web' ? { transition: 'border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease' } : {}) },
    inputContainerFocused: {
        borderColor: 'rgba(167,139,250,0.75)',
        backgroundColor: 'rgba(124,58,237,0.06)',
        ...(Platform.OS === 'web' ? { boxShadow: '0 0 0 3px rgba(124,58,237,0.22)' } : {}),
    },
    primaryButton: { borderRadius: 12, overflow: 'hidden', borderColor: 'rgba(120,160,255,0.35)', ...(Platform.OS === 'web' ? { transition: 'transform 200ms ease, border-color 200ms ease, opacity 200ms ease' } : {}) },
    buttonGradient: { paddingVertical: 14, borderRadius: 11, backgroundColor: 'rgba(59,130,246,0.18)' },
    primaryButtonText: { fontWeight: '700', letterSpacing: 0.3 },
    checkboxContainer: { minHeight: 44, gap: 10 },
    checkbox: { width: 18, height: 18, borderRadius: 5 },
    checkboxChecked: { backgroundColor: '#7C3AED', borderColor: '#A78BFA' },
    termsText: { lineHeight: 18 },
    backLink: { alignSelf: 'center', minHeight: 40, justifyContent: 'center', paddingHorizontal: 10, borderRadius: 8 },
    backLinkText: { fontSize: 13, fontWeight: '600', color: 'rgba(150, 185, 255, 0.95)' },
    termsLink: { minHeight: 32, justifyContent: 'center', paddingHorizontal: 6, borderRadius: 6 },
    termsLinkText: { fontSize: 12, fontWeight: '600', color: 'rgba(150, 185, 255, 0.95)', textDecorationLine: 'underline' },
};

const SHEETS = lookSheets({
    container: { flex: 1, backgroundColor: '#000000', borderWidth: 2, borderColor: '#000' },
    mainContent: { flex: 1, paddingTop: Platform.OS === 'web' ? 48 : 20, paddingHorizontal: Platform.OS === 'web' ? 48 : 20, justifyContent: 'center', alignItems: 'center' },
    headerSection: { alignItems: 'center', marginBottom: 32 },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: 32 },
    logoContainer: { width: 32, height: 32, justifyContent: 'center', alignItems: 'center' },
    brandTitle: { fontFamily: 'ClimateCrisis', fontSize: Platform.OS === 'web' ? 64 : 32, color: 'rgba(255,255,255,0.85)', lineHeight: Platform.OS === 'web' ? 64 : 36, textAlign: 'center' },
    formContainer: { width: '100%', maxWidth: 600, minHeight: 400, justifyContent: 'center', alignItems: 'center' },
    formCard: { width: '100%', height: '100%', padding: 48, backgroundColor: 'rgba(0,0,0,0.16)', justifyContent: 'center', alignItems: 'center' },
    formContent: { width: '100%', gap: 24, alignItems: 'center' },
    title: { fontSize: 28, fontWeight: '500', textAlign: 'center' },
    formSection: { width: '100%', gap: 16 },
    inputContainer: {
        borderWidth: 1,
        borderTopColor: 'rgba(255, 255, 255, 0.20)',
        borderLeftColor: 'rgba(255, 255, 255, 0.18)',
        borderRightColor: 'rgba(255, 255, 255, 0.12)',
        borderBottomColor: 'rgba(255, 255, 255, 0.08)', width: '100%', padding: 12, borderRadius: 4, backgroundColor: 'transparent', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)'
    },
    inputContainerWrapper: { width: '100%', gap: 16 },
    input: {
        fontSize: 15,
        color: '#fff',
        padding: 0,
        outlineStyle: 'none',
        ...(Platform.OS === 'web' && {
            // Override autofill styling
            WebkitBoxShadow: '0 0 0 1000px rgba(0,0,0,0.16) inset',
            WebkitTextFillColor: '#fff',
            transition: 'background-color 5000s ease-in-out 0s',
            // Placeholder styling
            '&::placeholder': {
                color: 'rgba(255, 255, 255, 0.20)',
                opacity: 1,
            },
            '&::-webkit-input-placeholder': {
                color: 'rgba(255, 255, 255, 0.20)',
                opacity: 1,
            },
            '&::-moz-placeholder': {
                color: 'rgba(255, 255, 255, 0.20)',
                opacity: 1,
            },
            '&:-ms-input-placeholder': {
                color: 'rgba(255, 255, 255, 0.20)',
                opacity: 1,
            },
            // Additional autofill overrides
            '&:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px rgba(0,0,0,0.16) inset',
                WebkitTextFillColor: '#fff',
                transition: 'background-color 5000s ease-in-out 0s',
            },
            '&:-webkit-autofill:hover': {
                WebkitBoxShadow: '0 0 0 1000px rgba(0,0,0,0.16) inset',
                WebkitTextFillColor: '#fff',
            },
            '&:-webkit-autofill:focus': {
                WebkitBoxShadow: '0 0 0 1000px rgba(0,0,0,0.16) inset',
                WebkitTextFillColor: '#fff',
            },
            '&:-webkit-autofill:active': {
                WebkitBoxShadow: '0 0 0 1000px rgba(0,0,0,0.16) inset',
                WebkitTextFillColor: '#fff',
            }
        })
    },
    primaryButton: {
        width: '100%',
        borderRadius: 4,
        borderWidth: 1,
        borderTopColor: 'rgba(255, 255, 255, 0.20)',
        borderLeftColor: 'rgba(255, 255, 255, 0.18)',
        borderRightColor: 'rgba(255, 255, 255, 0.12)',
        borderBottomColor: 'rgba(255, 255, 255, 0.08)',
    },
    buttonGradient: { padding: 8, borderRadius: 4, alignItems: 'center', justifyContent: 'center' },
    primaryButtonText: { fontSize: 15, fontWeight: '600', color: 'rgba(255,255,255,0.9)' },
    switchText: { fontSize: 13, color: 'rgba(255,255,255,0.8)', textAlign: 'center' },
    loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    buttonDisabled: {
        opacity: 0.6,
    },
    textDisabled: {
        opacity: 0.5,
    },
    termsContainer: {
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 8,
    },
    checkboxContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    checkbox: {
        width: 16,
        height: 16,
        borderRadius: 2,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.40)',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    checkboxChecked: {
        backgroundColor: 'rgba(255, 255, 255, 0.80)',
    },
    inputContainerFocused: {},
    backLink: {},
    backLinkText: {},
    termsLink: {},
    termsLinkText: {},
    termsText: {
        color: 'rgba(255, 255, 255, 0.40)',
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 16,
    },
    errorText: {
        color: '#ff6b6b',
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '400',
        textAlign: 'center',
        marginTop: 8,
    },
    successText: {
        color: '#51cf66',
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '400',
        textAlign: 'center',
        marginTop: 8,
    },
}, FL_OVERRIDES);

export default ForgotPasswordScreen;
